import React, { useState } from 'react'
import { Alert } from 'react-bootstrap'
import { Modal } from '../Modal/Modal'
import { Spinner } from '../Spinner/Spinner'
import { HumanInputFormView } from './HumanInputFormView'
import { hintsFromSchema, renderWorkflowOutputs } from './workflowOutputs'
import { isRunFinished, runStatusLabel } from './runStatus'
import { usePendingRunForm } from './usePendingRunForm'
import { useGetCustomWorkflowQuery, useGetCustomWorkflowRunQuery } from '../../store/customWorkflow/customWorkflows'

export interface ProvideInputModalProps {
  accountId: string
  workflowId: string
  runId: string
  title?: string
  show: boolean
  onHide: () => void
}

export const ProvideInputModal: React.FC<ProvideInputModalProps> = ({ accountId, workflowId, runId, title, show, onHide }) => {
  const [pollDone, setPollDone] = useState(false)

  const identity = `${accountId}|${workflowId}|${runId}`
  const [prevIdentity, setPrevIdentity] = useState(identity)
  if (prevIdentity !== identity) {
    setPrevIdentity(identity)
    setPollDone(false)
  }

  const { data: run, isError: runError } = useGetCustomWorkflowRunQuery(
    { accountId, workflowId, runId },
    { skip: !show || pollDone, pollingInterval: 2000, skipPollingIfUnfocused: true, refetchOnMountOrArgChange: true },
  )
  const { data: workflow } = useGetCustomWorkflowQuery({ accountId, workflowId }, { skip: !show })
  const finished = !!run && isRunFinished(run.status)
  if (finished && !pollDone) {
    setPollDone(true)
  }

  const pendingInput = usePendingRunForm({ accountId, workflowId, runId, run })
  const paused = run?.status === 'paused'

  const pausedBody = pendingInput.form ? (
    <>
      <HumanInputFormView
        key={pendingInput.form.id}
        accountId={accountId}
        workflowId={workflowId}
        form={pendingInput.form}
        submitting={pendingInput.submitting || pendingInput.locked}
        onSubmit={pendingInput.submit}
      />
      {pendingInput.submitError ? (
        <Alert variant='danger' className='mt-3 mb-0'>
          {pendingInput.submitError}
        </Alert>
      ) : null}
    </>
  ) : pendingInput.awaitingInput && !pendingInput.loadError ? (
    <Spinner size='sm' />
  ) : (
    <Alert variant='warning' className='mb-0'>
      No pending input was found for this run — it may have been answered already.
    </Alert>
  )

  const body = !run ? (
    runError ? (
      <Alert variant='warning' className='mb-0'>
        Could not load this run.
      </Alert>
    ) : (
      <Spinner size='sm' />
    )
  ) : finished ? (
    <div>
      <div className='fw-semibold mb-2'>Result — {runStatusLabel(run.status)}</div>
      {run.error ? (
        <Alert variant='danger' className='mb-0'>
          {run.error}
        </Alert>
      ) : (
        renderWorkflowOutputs(run.outputs, hintsFromSchema(workflow?.outputSchema), { accountId, workflowId, runId })
      )}
    </div>
  ) : paused ? (
    pausedBody
  ) : (
    <div className='d-flex align-items-center zocks-gap-2'>
      <Spinner size='sm' />
      <span>{pendingInput.submitted ? 'Input submitted — waiting for the workflow to finish…' : 'The workflow is running…'}</span>
    </div>
  )

  return <Modal size='lg' show={show} onHide={onHide} header={<h5 className='mb-0'>{title ?? 'Provide input'}</h5>} body={body} bodyClassName='pb-4' />
}

export default ProvideInputModal
